import { MutableRefObject, useEffect, useRef, useState } from 'react'
import Konva from 'konva'
import { VisualShape } from 'app/frontend/types/custom/VisualPlan'
import { MIN_SIZE } from '../constants'
import { applyTransform } from '../shapes/shapeTransform'
import { reflowTextGroup } from '../shapes/textLayout'

type Params = {
  nodeRefs: MutableRefObject<Map<string, Konva.Node>>
  selectedIds: string[]
  shapes: VisualShape[]
  width: number
  height: number
  canEdit: boolean
  tool: string
  onChange: (shapes: VisualShape[]) => void
}

export const useShapeTransformer = ({
  nodeRefs,
  selectedIds,
  shapes,
  width,
  height,
  canEdit,
  tool,
  onChange,
}: Params) => {
  const transformerRef = useRef<Konva.Transformer>(null)
  const [isResizing, setIsResizing] = useState(false)
  const shapesRef = useRef(shapes)
  shapesRef.current = shapes

  const handleTransformStart = () => {
    const anchor = transformerRef.current?.getActiveAnchor()
    setIsResizing(anchor !== 'rotater')
  }

  useEffect(() => {
    const transformer = transformerRef.current
    if (!transformer) return
    const nodes = selectedIds.flatMap((id) => {
      const node = nodeRefs.current.get(id)
      return node ? [node] : []
    })
    transformer.nodes(nodes)
    transformer.getLayer()?.batchDraw()
  }, [selectedIds, width, height, canEdit, tool, shapes, nodeRefs])

  const handleTransform = () => {
    const transformer = transformerRef.current
    if (!transformer) return
    const active = new Set(transformer.nodes())
    let reflowed = false
    selectedIds.forEach((id) => {
      const shape = shapesRef.current.find((entry) => entry.id === id)
      const node = nodeRefs.current.get(id)
      if (!shape || !node || !active.has(node) || shape.type !== 'text') return
      reflowTextGroup(node as Konva.Group)
      reflowed = true
    })
    if (reflowed) transformer.forceUpdate()
  }

  const handleTransformEnd = () => {
    setIsResizing(false)
    const transformer = transformerRef.current
    if (!transformer) return
    const transformedNodes = transformer.nodes()
    const updatedShapes = shapes.map((shape) => {
      const node = nodeRefs.current.get(shape.id)
      if (!node || !transformedNodes.includes(node)) return shape
      return applyTransform(shape, node, { width, height })
    })
    onChange(updatedShapes)
  }

  return {
    transformerRef,
    handleTransformStart,
    handleTransform,
    handleTransformEnd,
    isResizing,
    MIN_SIZE,
  }
}
